// Forms: a scanned paper form becomes a structured form (AI drafts, a person publishes), is signed on a
// phone by the installer and the owner, and is sealed and filed on the unit's record.
import React from 'react';
import { C, Explainer, PhoneFrame, Tap, Tick, rise, type ExplainerProps, type SceneDef, type SceneProps, type Unit } from './engine';
import { accentOf, docTitleOf, formRef, signersOf } from './maker';
import { shortName } from './unit';

const H0 = 650; // first highlight
const HG = 520; // highlight gap
const T = {
  publishTap: H0 + 5 * HG + 450, // 3700
  published: H0 + 5 * HG + 650,
  phone: 5200,
  check0: 5650,
  checkGap: 340,
  sheet: 7150,
  sig1: 7450,
  sig2: 8550,
  sigDur: 950,
  sealTap: 9750,
  done: 10050,
  wire: 10350,
  filed: 10600,
  sealed: 11050,
  verify: 11400,
};

const SIG_A =
  'M6 34 C 10 16, 18 6, 22 16 C 26 26, 18 40, 26 38 C 34 36, 36 18, 42 20 C 47 22, 42 34, 48 34 C 54 34, 56 22, 60 22 C 64 22, 62 32, 67 32 C 72 32, 74 20, 79 16 C 83 13, 84 24, 82 32 C 81 38, 88 34, 94 26 C 99 20, 104 24, 106 30 C 108 35, 116 28, 122 22 M 16 42 C 46 39, 90 37, 140 32';
const SIG_B =
  'M8 30 C 14 12, 24 10, 22 24 C 20 36, 12 38, 18 30 C 26 20, 34 22, 36 30 C 38 36, 44 36, 48 26 C 51 19, 56 20, 56 28 C 56 34, 62 34, 66 26 L 70 18 C 72 30, 74 34, 80 30 C 86 26, 88 20, 94 22 C 100 24, 96 34, 104 32 C 110 30, 114 22, 120 20';

/* paper texture: fractal noise + uneven scan light, as data URIs (no network) */
const NOISE =
  "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='.85' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 .32  0 0 0 0 .3  0 0 0 0 .24  0 0 0 .11 0'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")";
const PAPER = `${NOISE}, linear-gradient(103deg, rgba(0,0,0,.05) 0%, rgba(0,0,0,0) 18%, rgba(255,255,255,.35) 55%, rgba(0,0,0,.06) 100%), #F1EEE5`;

const INK = '#3B3A38';
const PW = 196;
const PH = 278;
/** PDF sections (page coords): unit, site, checks, notes, signatures. */
const SECTIONS: [number, number][] = [
  [50, 28],
  [82, 28],
  [114, 70],
  [190, 28],
  [224, 44],
];

const Bar = ({ w, o = 0.32, h = 3, mt = 0 }: { w: number | string; o?: number; h?: number; mt?: number }) => (
  <div style={{ width: w, height: h, borderRadius: 1, background: INK, opacity: o, marginTop: mt }} />
);
const Lbl = ({ children }: { children: React.ReactNode }) => (
  <div style={{ fontSize: 5.6, fontWeight: 700, letterSpacing: '.04em', textTransform: 'uppercase', color: INK, opacity: 0.78, fontFamily: 'Arial, Helvetica, sans-serif' }}>{children}</div>
);
const Field = ({ w }: { w: number | string }) => <div style={{ width: w, height: 9, borderBottom: `0.8px solid ${INK}`, opacity: 0.55 }} />;

function PaperForm({ u, hi, count }: { u: Unit; hi: number; count: number }) {
  const doc = docTitleOf(u);
  const [s1, s2] = signersOf(u);
  return (
    <div className="xp-abs" style={{ left: 22, top: 30, width: PW, height: PH, transform: 'rotate(-1.6deg)', transformOrigin: '50% 50%' }}>
      {/* page */}
      <div
        className="xp-abs"
        style={{
          inset: 0,
          background: PAPER,
          boxShadow: '0 1px 0 rgba(0,0,0,.04), 0 16px 30px -18px rgba(24,19,67,.55), 0 0 0 1px rgba(0,0,0,.05)',
          borderRadius: 2,
          overflow: 'hidden',
          fontFamily: 'Arial, Helvetica, sans-serif',
          color: INK,
          filter: 'contrast(1.04)',
        }}
      >
        {/* punch holes and a fold */}
        <span className="xp-abs" style={{ left: 6, top: 76, width: 6, height: 6, borderRadius: 6, background: 'rgba(36,30,97,.12)', boxShadow: 'inset 0 1px 1px rgba(0,0,0,.25)' }} />
        <span className="xp-abs" style={{ left: 6, top: 196, width: 6, height: 6, borderRadius: 6, background: 'rgba(36,30,97,.12)', boxShadow: 'inset 0 1px 1px rgba(0,0,0,.25)' }} />
        <span className="xp-abs" style={{ left: 0, right: 0, top: 139, height: 1, background: 'linear-gradient(90deg, rgba(0,0,0,.07), rgba(255,255,255,.5), rgba(0,0,0,.05))' }} />
        <div style={{ padding: '12px 14px 0 18px', filter: 'blur(.18px)' }}>
          <div className="xp-row" style={{ justifyContent: 'space-between' }}>
            <span style={{ fontSize: 8.5, fontWeight: 800, letterSpacing: '.14em', textTransform: 'uppercase' }}>{u.brand}</span>
            <span style={{ fontSize: 5.5, opacity: 0.6 }}>{formRef(u)}</span>
          </div>
          <div style={{ fontSize: 8.4, fontWeight: 700, marginTop: 6, letterSpacing: '-.005em', whiteSpace: 'nowrap' }}>{doc}</div>
          <Bar w="70%" o={0.18} h={2.5} mt={4} />
          {/* 1 unit */}
          <div className="xp-abs" style={{ left: 18, right: 14, top: SECTIONS[0][0] }}>
            <Lbl>1. Appliance</Lbl>
            <div className="xp-row" style={{ gap: 8, marginTop: 2 }}>
              <Field w="48%" />
              <Field w="44%" />
            </div>
            <div className="xp-row" style={{ gap: 8, marginTop: 1.5 }}>
              <Bar w={22} o={0.25} h={2} />
              <span style={{ width: '38%' }} />
              <Bar w={18} o={0.25} h={2} />
            </div>
          </div>
          {/* 2 site */}
          <div className="xp-abs" style={{ left: 18, right: 14, top: SECTIONS[1][0] }}>
            <Lbl>2. Site and customer</Lbl>
            <Field w="100%" />
            <Field w="62%" />
          </div>
          {/* 3 checks */}
          <div className="xp-abs" style={{ left: 18, right: 14, top: SECTIONS[2][0] }}>
            <Lbl>3. Checks completed</Lbl>
            {[0.8, 0.62, 0.7, 0.55, 0.66].map((w, i) => (
              <div key={i} className="xp-row" style={{ gap: 5, marginTop: 4.5 }}>
                <span style={{ width: 6, height: 6, border: `0.8px solid ${INK}`, opacity: 0.6, flex: 'none' }} />
                <Bar w={`${w * 70}%`} o={0.28} h={2.4} />
                <span style={{ flex: 1 }} />
                <span style={{ fontSize: 5, opacity: 0.5 }}>Y / N</span>
              </div>
            ))}
          </div>
          {/* 4 notes */}
          <div className="xp-abs" style={{ left: 18, right: 14, top: SECTIONS[3][0] }}>
            <Lbl>4. Readings and notes</Lbl>
            <Field w="100%" />
            <Field w="100%" />
          </div>
          {/* 5 signatures */}
          <div className="xp-abs" style={{ left: 18, right: 14, top: SECTIONS[4][0] }}>
            <Lbl>5. Declaration</Lbl>
            <Bar w="92%" o={0.16} h={2} mt={2} />
            <div className="xp-row" style={{ gap: 8, marginTop: 4 }}>
              {[s1.role, s2.role].map((r) => (
                <div key={r} style={{ flex: 1 }}>
                  <div style={{ height: 15, border: `0.8px solid ${INK}`, opacity: 0.45 }} />
                  <div style={{ fontSize: 5, marginTop: 1.5, opacity: 0.6 }}>{r} signature</div>
                </div>
              ))}
            </div>
          </div>
          <div className="xp-abs" style={{ left: 18, right: 14, bottom: 7 }}>
            <Bar w="58%" o={0.12} h={1.8} />
          </div>
        </div>
      </div>
      {/* AI highlight, following the rotation */}
      {SECTIONS.map(([y, h], i) => (
        <div
          key={i}
          className="xp-abs"
          style={{
            left: 13,
            right: 9,
            top: y - 3,
            height: h + 4,
            borderRadius: 4,
            background: 'rgba(63,174,108,.13)',
            boxShadow: `inset 0 0 0 1.4px ${C.emerald}`,
            opacity: hi === i ? 1 : 0,
            transition: 'opacity .2s',
          }}
        />
      ))}
      {SECTIONS.map(([y], i) => (
        <span
          key={`t${i}`}
          className="xp-abs xp-row"
          style={{ left: PW - 8, top: y - 2, width: 13, height: 13, borderRadius: 13, justifyContent: 'center', background: C.emerald, opacity: count > i ? 1 : 0, transform: count > i ? 'scale(1)' : 'scale(.5)', transition: 'opacity .18s, transform .3s var(--xe)' }}
        >
          <Tick on={count > i} size={9} />
        </span>
      ))}
    </div>
  );
}

function FormCard({ u, t }: { u: Unit; t: number }) {
  const at = (ms: number) => t >= ms;
  const shown = (i: number) => at(H0 + i * HG + 140);
  const published = at(T.published);
  const [s1, s2] = signersOf(u);
  const checks = u.checks.slice(0, 4);
  const row = (i: number, k: string, tag: string, body: React.ReactNode) => (
    <div className="xp-a" style={{ ...rise(shown(i), 0, 6), padding: '5px 12px', borderTop: `1px solid ${C.line}` }}>
      <div className="xp-row" style={{ justifyContent: 'space-between' }}>
        <span className="xp-cap" style={{ fontSize: 7, color: C.muted }}>
          {k}
        </span>
        <span className="xp-mono" style={{ fontSize: 6.6, color: C.emerald, letterSpacing: '.04em' }}>
          {tag}
        </span>
      </div>
      <div style={{ marginTop: 2 }}>{body}</div>
    </div>
  );
  return (
    <>
      <div className="xp-abs xp-cap" style={{ left: 252, top: 14, fontSize: 8.5, color: 'var(--xp-muted)' }}>
        Drafted from your PDF
      </div>
      <div className="xp-abs xp-card" style={{ left: 252, top: 32, width: 208, overflow: 'hidden' }}>
        <div style={{ padding: '9px 12px 7px' }}>
          <div style={{ fontSize: docTitleOf(u).length > 26 ? 10.5 : 11.5, fontWeight: 700, letterSpacing: '-.015em', whiteSpace: 'nowrap' }}>{docTitleOf(u)}</div>
          <div style={{ position: 'relative', height: 17, marginTop: 5 }}>
            <span
              className="xp-abs xp-row"
              style={{ left: 0, top: 0, gap: 5, height: 17, padding: '0 8px', borderRadius: 17, fontSize: 8, fontWeight: 600, whiteSpace: 'nowrap', background: '#FBF1DC', color: '#7A5410', visibility: published ? 'hidden' : 'visible' }}
            >
              <span style={{ width: 5, height: 5, borderRadius: 5, background: '#D4A045' }} />
              Draft · awaiting approval
            </span>
            <span
              className="xp-abs xp-row"
              style={{ left: 0, top: 0, gap: 5, height: 17, padding: '0 8px 0 3px', borderRadius: 17, fontSize: 8, fontWeight: 600, whiteSpace: 'nowrap', background: 'rgba(63,174,108,.14)', color: C.emerald, visibility: published ? 'visible' : 'hidden' }}
            >
              <span className="xp-row" style={{ width: 12, height: 12, borderRadius: 12, justifyContent: 'center', background: C.indigo, color: '#fff', fontSize: 6.5, fontWeight: 700 }}>
                S
              </span>
              Published by Sarah, Service team
            </span>
          </div>
        </div>
        {row(0, 'Unit', 'From record', <span className="xp-mono" style={{ fontSize: 8.5 }}>{u.model} · {u.serial}</span>)}
        {row(1, 'Site', 'Prefilled', <span style={{ fontSize: 9.5, fontWeight: 600 }}>{u.ownerShort}</span>)}
        {row(
          2,
          'Checks',
          `${checks.length} required`,
          <div>
            {checks.map((c) => (
              <div key={c} className="xp-row" style={{ gap: 6, height: 12, fontSize: 8.5, color: C.ink }}>
                <span style={{ width: 8, height: 8, borderRadius: 2.5, boxShadow: 'inset 0 0 0 1.2px #CFCDE2', flex: 'none' }} />
                <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c}</span>
              </div>
            ))}
          </div>
        )}
        {row(3, 'Readings and photos', 'Optional', <span style={{ fontSize: 8.5, color: C.muted }}>Numbers, notes, up to 6 photos</span>)}
        {row(
          4,
          'Signers',
          'In order',
          <div className="xp-row" style={{ gap: 5 }}>
            {[s1, s2].map((s, i) => (
              <span key={s.role} className="xp-row" style={{ gap: 4, height: 17, padding: '0 7px 0 4px', borderRadius: 17, fontSize: 8, fontWeight: 600, boxShadow: `inset 0 0 0 1px ${C.line}`, whiteSpace: 'nowrap' }}>
                <span className="xp-mono" style={{ fontSize: 6.5, color: C.muted }}>
                  {i + 1}
                </span>
                {s.role}
              </span>
            ))}
          </div>
        )}
        <div className="xp-row" style={{ justifyContent: 'space-between', padding: '7px 12px 8px', borderTop: `1px solid ${C.line}` }}>
          <span style={{ fontSize: 7.5, color: C.muted, lineHeight: 1.25, maxWidth: 118 }}>Goes live only when a person approves.</span>
          <span
            style={{
              position: 'relative',
              padding: '5px 11px',
              borderRadius: 20,
              fontSize: 8.5,
              fontWeight: 600,
              background: published ? C.alt : C.indigo,
              color: published ? C.muted : '#fff',
              transform: at(T.publishTap) && !published ? 'scale(.95)' : 'none',
              transition: 'transform .12s, background .2s, color .2s',
            }}
          >
            {published ? 'Live' : 'Publish'}
          </span>
        </div>
      </div>
      <Tap x={419} y={345} on={at(T.publishTap) && !published} />
    </>
  );
}

function Sig({ d, on, delay = 0 }: { d: string; on: boolean; delay?: number }) {
  return (
    <svg width="150" height="40" viewBox="0 0 150 46" style={{ position: 'absolute', left: 8, top: 2 }}>
      <path d={d} pathLength={1} fill="none" stroke={C.indigo} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" strokeDasharray="1" strokeDashoffset={on ? 0 : 1} style={{ transition: on ? `stroke-dashoffset ${T.sigDur}ms cubic-bezier(.45,.1,.4,1) ${delay}ms` : 'none' }} />
    </svg>
  );
}

function FormsScene({ t, tone, u }: SceneProps) {
  const at = (ms: number) => t >= ms;
  const dark = tone === 'dark';
  const accent = accentOf(u);
  const doc = docTitleOf(u);
  const [s1, s2] = signersOf(u);
  const checks = u.checks.slice(0, 4);
  const beat2 = at(T.phone);
  const count = SECTIONS.filter((_, i) => at(H0 + i * HG)).length;
  const current = at(H0 + SECTIONS.length * HG) ? -1 : count - 1;
  const ticked = checks.filter((_, i) => at(T.check0 + i * T.checkGap)).length;
  const done = at(T.done);
  const sealed = at(T.sealed);

  return (
    <>

      {/* ---------- beat 1: paper → structured form ---------- */}
      <div style={{ display: beat2 ? 'none' : 'block' }}>
        <div className="xp-abs xp-cap" style={{ left: 26, top: 12, fontSize: 8.5, color: 'var(--xp-muted)' }}>
          Your PDF, as it is today
        </div>
        <PaperForm u={u} hi={current} count={count} />
        <svg className="xp-abs" style={{ left: 0, top: 0 }} width={480} height={360}>
          <path d="M222 168 C 236 168, 236 150, 250 150" fill="none" stroke={dark ? C.lime : C.emerald} strokeWidth="1.25" strokeDasharray="3 3" opacity={at(H0) ? 1 : 0} style={{ transition: 'opacity .2s' }} />
        </svg>
        <FormCard u={u} t={t} />
      </div>

      {/* ---------- beat 2: signed on a phone, sealed on the record ---------- */}
      <div style={{ display: beat2 ? 'block' : 'none' }}>
        <PhoneFrame x={34} y={18} w={206} h={392} tone={tone}>
          <div className="xp-abs" style={{ inset: 0, padding: '34px 14px 0', visibility: done ? 'hidden' : 'visible' }}>
            <div className="xp-row" style={{ gap: 6 }}>
              <span className="xp-row" style={{ width: 16, height: 16, borderRadius: 4, justifyContent: 'center', background: accent, color: '#fff', fontSize: 8.5, fontWeight: 800 }}>
                {u.brand[0]}
              </span>
              <span style={{ fontSize: 8.5, fontWeight: 800, letterSpacing: '.14em', textTransform: 'uppercase', color: accent }}>{u.brand}</span>
            </div>
            <div style={{ fontSize: 13, fontWeight: 700, letterSpacing: '-.02em', marginTop: 8, lineHeight: 1.12 }}>{doc}</div>
            <div className="xp-mono" style={{ fontSize: 8, color: C.muted, marginTop: 3 }}>
              {u.serial} · {shortName(u.partner, 22)}
            </div>
            <div className="xp-row" style={{ marginTop: 9, gap: 8 }}>
              <div style={{ flex: 1, height: 4, borderRadius: 4, background: C.line, overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${(ticked / checks.length) * 100}%`, background: accent, transition: 'width .35s var(--xe)' }} />
              </div>
              <span className="xp-mono" style={{ fontSize: 8, color: C.muted }}>
                {ticked}/{checks.length}
              </span>
            </div>
            <div style={{ marginTop: 5 }}>
              {checks.map((c, i) => {
                const on = at(T.check0 + i * T.checkGap);
                return (
                  <div key={c} className="xp-row" style={{ gap: 8, height: 25, borderBottom: `1px solid ${C.line}`, fontSize: 10, fontWeight: 500, color: on ? C.indigo : C.muted, transition: 'color .2s' }}>
                    <span className="xp-row" style={{ width: 15, height: 15, flex: 'none', borderRadius: 4, justifyContent: 'center', background: on ? accent : '#fff', boxShadow: on ? 'none' : 'inset 0 0 0 1.5px #CFCDE2', transition: 'background .18s' }}>
                      <Tick on={on} size={10} />
                    </span>
                    <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c}</span>
                  </div>
                );
              })}
            </div>
            {/* signature sheet: both signers */}
            <div
              className="xp-abs"
              style={{
                left: 0,
                right: 0,
                bottom: 0,
                height: 236,
                background: '#fff',
                borderRadius: '16px 16px 0 0',
                boxShadow: '0 -14px 30px -18px rgba(24,19,67,.45), 0 0 0 1px #ECEBF5',
                padding: '8px 14px',
                transform: at(T.sheet) ? 'translateY(0)' : 'translateY(105%)',
                transition: 'transform .5s var(--xe)',
              }}
            >
              <div style={{ width: 30, height: 3, borderRadius: 3, background: C.line, margin: '0 auto 7px' }} />
              {[
                [s1, SIG_A, T.sig1],
                [s2, SIG_B, T.sig2],
              ].map(([s, d, when], i) => {
                const sg = s as { role: string; name: string };
                const signed = at((when as number) + T.sigDur);
                return (
                  <div key={i} style={{ marginTop: i ? 7 : 0 }}>
                    <div className="xp-row" style={{ justifyContent: 'space-between' }}>
                      <span className="xp-cap" style={{ fontSize: 7.2, color: C.muted }}>
                        {sg.role}
                      </span>
                      <span className="xp-row" style={{ gap: 4, fontSize: 8.5, fontWeight: 600, maxWidth: 104, whiteSpace: 'nowrap', overflow: 'hidden' }}>
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{shortName(sg.name, 16)}</span>
                        <span style={{ width: 11, height: 11, borderRadius: 11, flex: 'none', display: 'grid', placeItems: 'center', background: signed ? C.emerald : 'transparent', transition: 'background .2s' }}>
                          <Tick on={signed} size={8} />
                        </span>
                      </span>
                    </div>
                    <div style={{ position: 'relative', height: 44, marginTop: 4, borderRadius: 8, background: C.alt, boxShadow: `inset 0 0 0 1px ${C.line}` }}>
                      <Sig d={d as string} on={at(when as number)} />
                      <span className="xp-abs" style={{ left: 10, right: 10, bottom: 9, height: 1, background: '#D9D8E8' }} />
                    </div>
                  </div>
                );
              })}
              <div style={{ marginTop: 9, height: 30, borderRadius: 30, background: accent, color: '#fff', fontSize: 10.5, fontWeight: 600, display: 'grid', placeItems: 'center', transform: at(T.sealTap) ? 'scale(.97)' : 'none', transition: 'transform .12s' }}>
                Sign and seal
              </div>
              <Tap x={89} y={192} on={at(T.sealTap) && !done} />
            </div>
          </div>

          {/* done (cut) */}
          <div className="xp-abs" style={{ inset: 0, padding: '38px 14px 0', visibility: done ? 'visible' : 'hidden', background: '#fff' }}>
            <div className="xp-a" style={{ ...rise(done, 0, 10), width: 42, height: 42, borderRadius: 42, background: accent, display: 'grid', placeItems: 'center' }}>
              <Tick on={at(T.done + 150)} size={22} />
            </div>
            <div className="xp-a" style={{ ...rise(done, 80), marginTop: 11, fontSize: 17, fontWeight: 800, letterSpacing: '-.03em', lineHeight: 1.08 }}>
              Signed by both.
              <br />
              Sealed.
            </div>
            <div className="xp-a xp-mono" style={{ ...rise(done, 150), marginTop: 5, fontSize: 8, color: C.muted }}>
              {u.model} · {u.serial}
            </div>
            <div className="xp-a" style={{ ...rise(done, 230), marginTop: 12, borderRadius: 10, boxShadow: `inset 0 0 0 1px ${C.line}` }}>
              {[
                [s1.role, `${shortName(s1.name, 22)} · 14:32`],
                [s2.role, `${shortName(s2.name, 22)} · 14:33`],
                ['Copy sent to', 'Both signers, by email'],
              ].map(([k, v], i) => (
                <div key={k} style={{ padding: '6px 10px', borderTop: i ? `1px solid ${C.line}` : 'none' }}>
                  <div className="xp-cap" style={{ fontSize: 7, color: C.muted }}>
                    {k}
                  </div>
                  <div style={{ fontSize: 9.5, fontWeight: 600, marginTop: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{v}</div>
                </div>
              ))}
            </div>
          </div>
        </PhoneFrame>

        <svg className="xp-abs" style={{ left: 0, top: 0 }} width={480} height={360}>
          <path d="M244 214 C 256 214, 254 186, 268 186" fill="none" stroke={dark ? C.lime : C.emerald} strokeWidth="1.25" strokeDasharray="60" strokeDashoffset={at(T.wire) ? 0 : 60} style={{ transition: 'stroke-dashoffset .4s var(--xe)' }} />
        </svg>

        {/* the unit's record */}
        <div className="xp-abs xp-cap" style={{ left: 268, top: 50, fontSize: 8.5, color: 'var(--xp-muted)' }}>
          Filed on the unit
        </div>
        <div className="xp-abs xp-card" style={{ left: 268, top: 68, width: 196, overflow: 'hidden' }}>
          <div style={{ padding: '11px 12px 9px' }}>
            <div style={{ fontSize: 11.5, fontWeight: 700, letterSpacing: '-.01em' }}>{u.title}</div>
            <div className="xp-mono" style={{ fontSize: 8, color: C.muted, marginTop: 2 }}>
              {u.serial}
            </div>
          </div>
          {/* the new document */}
          <div style={{ padding: '9px 12px', borderTop: `1px solid ${C.line}`, background: at(T.filed) && !at(T.filed + 900) ? 'rgba(63,174,108,.07)' : 'transparent', transition: 'background .5s' }}>
            <div className="xp-row" style={{ gap: 8, alignItems: 'flex-start' }}>
              <svg width="18" height="22" viewBox="0 0 18 22" style={{ flex: 'none', marginTop: 1 }}>
                <path d="M2 1.5h9.5L16 6v14.5H2z" fill="#fff" stroke={sealed ? C.emerald : '#C9C7DD'} strokeWidth="1.2" strokeLinejoin="round" />
                <path d="M11.5 1.5V6H16" fill="none" stroke={sealed ? C.emerald : '#C9C7DD'} strokeWidth="1.2" strokeLinejoin="round" />
                <path d="M5 11h8M5 14h8M5 17h5" stroke="#C9C7DD" strokeWidth="1.1" strokeLinecap="round" />
              </svg>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ fontSize: 9.5, fontWeight: 600, lineHeight: 1.2 }}>{doc}</div>
                <div style={{ position: 'relative', height: 13, marginTop: 2, fontSize: 8, overflow: 'hidden' }}>
                  <span className="xp-a" style={{ position: 'absolute', whiteSpace: 'nowrap', color: C.muted, transform: sealed ? 'translateY(-12px)' : 'none', opacity: sealed ? 0 : 1 }}>
                    Awaiting 2 signatures
                  </span>
                  <span className="xp-a" style={{ position: 'absolute', whiteSpace: 'nowrap', color: C.emerald, fontWeight: 600, transform: sealed ? 'none' : 'translateY(12px)', opacity: sealed ? 1 : 0 }}>
                    Sealed · audit page attached
                  </span>
                </div>
              </div>
            </div>
            <div className="xp-a xp-row" style={{ ...rise(at(T.verify), 0, 4), gap: 6, marginTop: 8, marginLeft: 26 }}>
              <span className="xp-row" style={{ gap: 4, height: 18, padding: '0 8px', borderRadius: 18, fontSize: 8, fontWeight: 600, color: C.indigo, boxShadow: `inset 0 0 0 1px ${C.line}` }}>
                <svg width="9" height="10" viewBox="0 0 9 10" fill="none">
                  <path d="M4.5 1 8 2.4v2.8c0 2-1.5 3.4-3.5 3.9C2.5 8.6 1 7.2 1 5.2V2.4z" stroke={C.emerald} strokeWidth="1.1" />
                </svg>
                Verify
              </span>
              <span className="xp-mono" style={{ fontSize: 7, color: C.muted }}>
                Document hash recorded
              </span>
            </div>
          </div>
          {u.history.slice(0, 2).map(([k, d]) => (
            <div key={k} className="xp-row" style={{ justifyContent: 'space-between', padding: '7px 12px', borderTop: `1px solid ${C.line}`, fontSize: 8.5, color: C.muted }}>
              <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 120 }}>{k}</span>
              <span className="xp-mono" style={{ fontSize: 7.5 }}>
                {d}
              </span>
            </div>
          ))}
        </div>
        <div className="xp-abs xp-row xp-cap xp-a" style={{ ...rise(at(T.verify + 300), 0, 6), left: 270, top: 314, gap: 7, fontSize: 8, color: 'var(--xp-muted)' }}>
          <svg width="12" height="10" viewBox="0 0 12 10" fill="none">
            <path d="M1 1v3.5a2 2 0 0 0 2 2h7.5M8 4l2.6 2.5L8 9" stroke={dark ? C.lime : C.emerald} strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          Also emailed, as today
        </div>
      </div>
    </>
  );
}

export const formsDef: SceneDef = {
  name: 'Forms',
  label: (u) => {
    const [s1, s2] = signersOf(u);
    return `A scanned ${docTitleOf(u).toLowerCase()} for a ${u.brand} ${u.title}: AI picks out its fields and drafts a structured form, a person on the service team approves and publishes it, then the ${s1.role.toLowerCase()} ticks the checks on a phone, the ${s1.role.toLowerCase()} and the ${s2.role.toLowerCase()} both sign, and the sealed document with an audit page and a verify link is filed on serial ${u.serial}.`;
  },
  W: 480,
  H: 360,
  duration: 14000,
  end: 13300,
  steps: [
    { at: 0, label: 'Your PDF' },
    { at: H0, label: 'Fields drafted' },
    { at: T.published, label: 'Approved by a person' },
    { at: T.phone, label: 'Signed on a phone' },
    { at: T.done, label: 'Sealed and filed' },
  ],
  Scene: FormsScene,
};

export const FormsExplainer = (p: ExplainerProps) => <Explainer def={formsDef} {...p} />;
